/* eslint-disable no-param-reassign */
/* eslint-disable react/display-name */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Tooltip as ReactTooltip } from "react-tooltip";

import * as userActions from "../../../actions/userActions";
import * as reportActions from "../../../actions/reportActions";
import * as codeActions from "../../../actions/codeActions";
import * as exportActions from "../../../actions/exportActions";

import { formatDate } from "../../../helpers/date";

import Loading from "../../common/Loading";

import Filters from "./CustomerProjectStatusFilters";

const CustomerProjectStatus = ({
  actions,
  report,
  auth,
  codes,
  loading,
  exported,
  exporting
}) => {
  const [filters, setFilters] = useState({
    department: [],
    project_manager: null
  });

  useEffect(() => {
    if (!codes?.length) {
      actions.loadCodes();
    }
  }, [codes, actions]);

  useEffect(() => {
    if (exported) {
      const url = window.URL.createObjectURL(
        new Blob([exported], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `Customer-Project-Status-${new Date().getTime()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      actions.clearExported();
    }
  }, [exported, actions]);

  const handleChangeFilters = (field, input) => {
    setFilters((prev) => ({
      ...prev,
      [field]: Array.isArray(input)
        ? input.map((i) => ({ label: i.label, value: i.value }))
        : input
    }));
  };

  const resetFilters = () => {
    setFilters({ department: [], project_manager: null });
    actions.clearReport();
  };

  const getInitials = (text) =>
    text
      ?.split(/\s/)
      .reduce((response, word) => (response += word.slice(0, 1)), "");

  const generateTooltip = (id) => (
    <ReactTooltip
      anchorId={id}
      className={`custom-tooltip ${auth.isNightMode ? "dark" : "light"}`}
      place="left"
      type={auth.isNightMode ? "dark" : "light"}
      effect="solid"
      globalEventOff="click"
    />
  );

  const getMostRecentNote = (notes) => {
    const mostRecentNote = notes
      .filter((n) => n.is_manager_note)
      .reduce(
        (a, b) => (new Date(a.created_on) > new Date(b.created_on) ? a : b),
        0
      );
    return mostRecentNote?.note
      ? {
          note: mostRecentNote.note,
          date: `As of ${formatDate(mostRecentNote.created_on, "MM/dd/yyyy")}`
        }
      : "";
  };

  const handleExportPDF = () => {
    const queryFilters = {
      filters
    };
    const payload = {
      data: queryFilters,
      type: "CUSTOMER_PROJECT_STATUS",
      format: "pdf"
    };
    actions.performExport(payload);
  };

  return (
    <React.Fragment>
      <Filters
        filters={filters}
        onChange={handleChangeFilters}
        onFetchClick={() => actions.getCustomerProjectStatusReport({ filters })}
        onResetClick={resetFilters}
        loading={loading}
        onPDFExport={handleExportPDF}
        exportDisabled={loading || exporting || !report?.data?.length}
      />
      {loading ? (
        <div className="cps-loading">
          <Loading />
        </div>
      ) : null}
      {!loading && report?.data ? (
        <div className="content-wrapper cps-wrapper">
          <div>
            {report.data?.map(({ customers, department, _id }) => (
              <div key={_id}>
                <div className="dep-name">{department.description}</div>
                <div>
                  {customers?.map(
                    ({ customer, projects, _id: customer_id }) => (
                      <div key={customer_id.customer}>
                        {projects.map((o, i) => (
                          <div key={i}>
                            {i === 0 && (
                              <div className="cust-name">{customer.name}</div>
                            )}

                            <div className="proj-name">
                              {o?.name}{" "}
                              <span className="extra-info">
                                - Start Date: {o.start_date};{" "}
                              </span>
                              <span
                                id={`salesperson-${o.salesperson}`}
                                data-tooltip-variant={
                                  auth.isNightMode ? "dark" : "light"
                                }
                                className="extra-info"
                                data-tooltip-content={`Sales: ${
                                  o.salesperson || ""
                                }`}
                                data-html
                              >
                                Sales: {getInitials(o.salesperson)};{" "}
                              </span>
                              {generateTooltip(`salesperson-${o.salesperson}`)}
                              <span
                                id={`project_manager-${o.project_manager}`}
                                className="extra-info"
                                data-tooltip-content={`Project Manager: ${o.project_manager}`}
                                data-tooltip-variant={
                                  auth.isNightMode ? "dark" : "light"
                                }
                                data-html
                              >
                                PM: {getInitials(o.project_manager)};{" "}
                              </span>
                              {generateTooltip(
                                `project_manager-${o.project_manager}`
                              )}
                              {o.resource1 && (
                                <span
                                  id={`resource1-${o.resource1}`}
                                  className="extra-info"
                                  data-tooltip-content={`Resource 1: ${o.resource1}`}
                                  data-tooltip-variant={
                                    auth.isNightMode ? "dark" : "light"
                                  }
                                  data-html
                                >
                                  Resource: {getInitials(o.resource1)}
                                </span>
                              )}
                              {generateTooltip(`resource1-${o.resource1}`)}
                              {o.resource2 && (
                                <span
                                  id={`resource2-${o.resource2}`}
                                  className="extra-info"
                                  data-tooltip-content={`Resource 2: ${o.resource2}`}
                                  data-tooltip-variant={
                                    auth.isNightMode ? "dark" : "light"
                                  }
                                  data-html
                                >
                                  {o.resource1 ? "/ " : "Resource: "}
                                  {getInitials(o.resource2)}
                                </span>
                              )}
                              {generateTooltip(`resource2-${o.resource2}`)}
                              <span className="extra-info">
                                {getMostRecentNote(o.notes)?.date}
                              </span>
                            </div>

                            {getMostRecentNote(o.notes)?.note && (
                              <div style={{ padding: "10px 0px 10px 40px" }}>
                                <div
                                  className="extra-info"
                                  dangerouslySetInnerHTML={{
                                    __html: getMostRecentNote(o.notes)?.note
                                  }}
                                />
                              </div>
                            )}

                            <div className="bill-name extra-info">
                              {o.budget_hours > 0 &&
                                o.hours_consumed !== undefined && (
                                  <div>
                                    Project balance of{" "}
                                    <span
                                      className={
                                        o.budget_hours - o.hours_consumed < 0
                                          ? "error"
                                          : ""
                                      }
                                    >{`${(
                                      o.budget_hours - o.hours_consumed
                                    ).toFixed(1)}`}</span>
                                    {` of ${o.budget_hours} hour budget`}
                                  </div>
                                )}
                              {o.contractPrepaidBalance &&
                                o.contractPrepaidDate &&
                                o.contractPrepaidPurchase && (
                                  <div>
                                    Prepaid balance of{" "}
                                    {o.contractPrepaidBalance} from last
                                    purchase of {o.contractPrepaidPurchase}{" "}
                                    hours on{" "}
                                    {formatDate(
                                      o.contractPrepaidDate,
                                      "MM/dd/yyyy"
                                    )}
                                    {o.contractPrepaidBalance <
                                    o.contractPrepaidPurchase * 0.1 ? (
                                      <span className="error">
                                        {" "}
                                        *** Time to arrange another purchase
                                      </span>
                                    ) : (
                                      ""
                                    )}
                                  </div>
                                )}
                              {o.call_type === "MANAGED SERVICES" && (
                                <div>
                                  {o.call_type}{" "}
                                  {` - Total actual hours: ${o.actualHours}`}
                                </div>
                              )}
                              {o.call_type !== "MANAGED SERVICES" &&
                                o.budget_hours === 0 &&
                                o.hours_consumed !== undefined && (
                                  <React.Fragment>
                                    <div>
                                      {`${o.hours_consumed} hours used to date`}
                                    </div>
                                    {o.call_type !== "PREPAID" &&
                                      o.call_type && <div>{o.call_type}</div>}
                                  </React.Fragment>
                                )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </React.Fragment>
  );
};

CustomerProjectStatus.propTypes = {
  actions: PropTypes.object,
  report: PropTypes.object,
  auth: PropTypes.object,
  codes: PropTypes.array,
  loading: PropTypes.bool,
  exported: PropTypes.bool,
  exporting: PropTypes.bool
};

CustomerProjectStatus.defaultProps = {
  actions: {},
  report: {},
  auth: {}
};

function mapStatesToProps(state) {
  return {
    report: state.reducers.report,
    auth: state.reducers.auth,
    codes: state.reducers.codes,
    loading: state.reducers.loading,
    exported: state.reducers.exported,
    exporting: state.reducers.exporting
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        ...userActions,
        ...reportActions,
        ...codeActions,
        ...exportActions
      },
      dispatch
    )
  };
}

export default connect(
  mapStatesToProps,
  mapDispatchToProps
)(CustomerProjectStatus);
